// =====================================================
// CSV PARSER
// Stage 2: Participants & Pricing
// Parse and validate CSV files for batch import
// =====================================================

export interface CSVRow {
  [columnName: string]: string;
}

export interface ParsedCSVData {
  headers: string[];
  rows: CSVRow[];
}

/**
 * Parse CSV file into structured data
 */
export async function parseCSV(file: File): Promise<ParsedCSVData> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());

        if (lines.length === 0) {
          throw new Error('El archivo está vacío');
        }

        // Parse headers (first line)
        const headers = parseCSVLine(lines[0]);
        
        if (headers.length === 0) {
          throw new Error('No se encontraron columnas en el archivo');
        }

        // Parse data rows
        const rows: CSVRow[] = [];
        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i]);
          
          // Skip empty rows
          if (values.every(v => !v.trim())) {
            continue;
          }

          // Create row object
          const row: CSVRow = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || '';
          });
          
          rows.push(row);
        }

        if (rows.length === 0) {
          throw new Error('No se encontraron filas de datos en el archivo');
        }

        if (rows.length > 1000) {
          throw new Error('El archivo contiene más de 1000 filas. Por favor divide el archivo en partes más pequeñas.');
        }

        resolve({ headers, rows });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Error al leer el archivo'));
    };

    reader.readAsText(file, 'UTF-8');
  });
}

/**
 * Parse a single CSV line handling quoted values and commas
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      // Handle escaped quotes
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++; // Skip next quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  // Add last field
  result.push(current.trim());

  return result;
}

/**
 * Generate CSV string from data
 */
export function generateCSV(headers: string[], rows: string[][]): string {
  const escapeCellValue = (value: string): string => {
    // Escape if contains comma, quote, or newline
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const headerRow = headers.map(escapeCellValue).join(',');
  const dataRows = rows.map(row => row.map(escapeCellValue).join(',')).join('\n');

  return `${headerRow}\n${dataRows}`;
}
